import type { ReactNode, Ref, ChangeEvent } from "react";
import React, { useRef, useEffect, useImperativeHandle } from "react";
import type { TextAreaProps as AriaTextAreaProps, TextFieldProps as AriaTextFieldProps } from "react-aria-components";
import { TextArea as AriaTextArea, TextField as AriaTextField } from "react-aria-components";
import { HintText } from "@/components/base/input/hint-text";
import { Label } from "@/components/base/input/label";
import { cx } from "@/lib/utils/cx";

// Creates a data URL for an SVG resize handle with a given color.
const getResizeHandleBg = (color: string) => {
    return `url(data:image/svg+xml;base64,${btoa(`<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 2L2 10" stroke="${color}" stroke-linecap="round"/><path d="M11 7L7 11" stroke="${color}" stroke-linecap="round"/></svg>`)})`;
};

interface TextAreaBaseProps extends Omit<AriaTextAreaProps, 'onChange'> {
    ref?: Ref<HTMLTextAreaElement>;
    /** Auto resize the textarea to fit its content */
    autoResize?: boolean;
    /** onChange handler */
    onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    /** Current value (for auto-resize tracking) */
    currentValue?: string;
}

export const TextAreaBase = ({ className, autoResize = false, onChange, ref, currentValue, ...props }: TextAreaBaseProps) => {
    const internalRef = useRef<HTMLTextAreaElement>(null);
    
    // Expose the internal ref to parent components
    useImperativeHandle(ref, () => internalRef.current as HTMLTextAreaElement);

    // Auto-resize the textarea to fit content
    const adjustHeight = () => {
        const textarea = internalRef.current;
        if (textarea && autoResize) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        }
    };

    // Adjust height on mount and when value changes
    useEffect(() => {
        if (autoResize) {
            // Use requestAnimationFrame to ensure DOM is ready
            requestAnimationFrame(() => {
                adjustHeight();
            });
        }
    }, [autoResize, currentValue]);

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        onChange?.(e);
        if (autoResize) {
            adjustHeight();
        }
    };

    return (
        <AriaTextArea
            {...props}
            ref={internalRef}
            onChange={handleChange}
            rows={autoResize ? 1 : props.rows}
            style={
                {
                    "--resize-handle-bg": getResizeHandleBg("#D5D7DA"),
                    "--resize-handle-bg-dark": getResizeHandleBg("#373A41"),
                } as React.CSSProperties
            }
            className={(state) =>
                cx(
                    "w-full scroll-py-3 rounded-lg border border-gray-300 bg-white px-3.5 py-3 text-md text-primary shadow-xs transition duration-100 ease-linear placeholder:text-placeholder autofill:rounded-lg autofill:text-primary focus:outline-hidden dark:border-gray-700 dark:bg-gray-900",

                    // Resize handle (hidden when autoResize)
                    !autoResize && "[&::-webkit-resizer]:bg-(image:--resize-handle-bg) [&::-webkit-resizer]:bg-contain dark:[&::-webkit-resizer]:bg-(image:--resize-handle-bg-dark)",
                    autoResize && "resize-none overflow-hidden",

                    state.isFocused && !state.isDisabled && "border-brand-500 ring-1 ring-brand-500 dark:border-brand-400 dark:ring-brand-400",
                    state.isDisabled && "cursor-not-allowed bg-gray-50 text-gray-500 dark:bg-gray-800",
                    state.isInvalid && "border-error-500",
                    state.isInvalid && state.isFocused && "ring-1 ring-error-500",

                    typeof className === "function" ? className(state) : className,
                )
            }
        />
    );
};

TextAreaBase.displayName = "TextAreaBase";

interface TextFieldProps extends AriaTextFieldProps {
    /** Label text for the textarea */
    label?: string;
    /** Helper text displayed below the textarea */
    hint?: ReactNode;
    /** Tooltip message displayed after the label. */
    tooltip?: string;
    /** Class name for the textarea wrapper */
    textAreaClassName?: TextAreaBaseProps["className"];
    /** Ref for the textarea wrapper */
    ref?: Ref<HTMLDivElement>;
    /** Ref for the textarea */
    textAreaRef?: TextAreaBaseProps["ref"];
    /** Whether to hide required indicator from label. */
    hideRequiredIndicator?: boolean;
    /** Placeholder text. */
    placeholder?: string;
    /** Visible height of textarea in rows . */
    rows?: number;
    /** Visible width of textarea in columns. */
    cols?: number;
    /** Auto resize the textarea to fit its content */
    autoResize?: boolean;
}

export const TextArea = ({
    label,
    hint,
    tooltip,
    textAreaRef,
    hideRequiredIndicator,
    textAreaClassName,
    placeholder,
    className,
    rows,
    cols,
    autoResize,
    value,
    ...props
}: TextFieldProps) => {
    return (
        <AriaTextField
            {...props}
            value={value}
            aria-label={!label ? props["aria-label"] ?? placeholder : undefined}
            className={(state) =>
                cx("group flex h-max w-full flex-col items-start justify-start gap-1.5", typeof className === "function" ? className(state) : className)
            }
        >
            {({ isInvalid, isRequired }) => (
                <>
                    {label && (
                        <Label isRequired={hideRequiredIndicator ? !hideRequiredIndicator : isRequired} tooltip={tooltip}>
                            {label}
                        </Label>
                    )}

                    <TextAreaBase placeholder={placeholder} className={textAreaClassName} ref={textAreaRef} rows={rows} cols={cols} autoResize={autoResize} currentValue={value} />

                    {hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}
                </>
            )}
        </AriaTextField>
    );
};

TextArea.displayName = "TextArea";
